import { Injectable, OnDestroy } from '@angular/core';

import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import moment from 'moment-timezone';

import { DistributionService } from '../../TargetingShared/services/distribution.service';
import { BlockModel } from '../../PageBuilder/models/block.model';
import { DataPointModel } from '../../Charts/models/data-point.model';
import { ChartDisplayElementModel } from '../../Reports/models/chart-display-element.model';
import { ReportBlockTypeEnum } from '../../Reports/enums/report-block-type.enum';
import { TargetingEventsEnum, userEventToIconMap, UserEventTypesEnum } from '../../Reports/enums/user_event.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { TargetingSearchesService } from '../../../Discover/Targeting/services/targeting-searches.service';
import { TargetingListService } from '../../../Discover/Targeting/services/targeting-list.service';
import { UsersService } from '../../../Core/Users/services/users.service';
import { DistributionStatus } from '../../TargetingShared/enum/distribution-status.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { UserModel } from '../../../Core/Users/models/user.model';
import { FilterService } from '../../Filter/services/abstract-filter.service';
import { FilterTypeTextModel } from '../../Filter/models/filter-type-text.model';
import { FixedChartSourceFiltersEnum } from '../enums/fixed-chart-source-filters.enum';
import { FilterTypeCheckboxModel } from '../../Filter/models/filter-type-checkbox.model';
import { FilterTypeDateQueryModel } from '../../Filter/models/filter-type-date-query.model';
import { SearchModeEnum } from '../../TargetingShared/enum/search-mode.enum';
import { ListTypeEnum } from '../../TargetingShared/enum/list-type.enum';
import { ChartContentFilterModel } from '../models/chart-content-filter.model';
import { DateQueryTypeEnum } from '../../../api/mentions/enum/date-query-type.enum';
import { DistributionSummaryModel } from '../../TargetingShared/model/distribution-summary-model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { UserEventActionTypeEnum } from '../../../api/events/enum/action-type.enum';
import { EventResourceTypeEnum } from '../../../api/events/enum/event-resource-type.enum';
import { EventsService } from '../../../Core/Users/services/events.service';
import { DistributionType } from '../../TargetingShared/enum/distribution-type.enum';
import { RecipientReportDataSeries } from '../../TargetingShared/components/statisticReport/RecipientReport/recipient.report.data.model';
import { getDefaultDateInputOptionValuesModel } from '../../Forms/models/date-query-input-options.model';
import { BriefingsApi } from '../../../api/briefings/briefings.api';

interface FixedSourceResult {
    id?: number;
    name?: string;
    status?: DistributionStatus;
    hasContacts?: boolean;
    hasOutlets?: boolean;
    type?: SearchModeEnum | DistributionType;
    createdByUserId?: number;
    createdDate?: Date;
    modifiedByUserId?: number;
    modifiedDate?: Date;
    count?: number;
    targetingEventType?: TargetingEventsEnum;
    sourceType?: UserEventTypesEnum;
}


// Some charts like recent targeting activity chart have fixed data source and do not need to set up series
// Here we use this service this method to return data directly

@Injectable()
export class FixedChartSourceDataService extends FilterService  implements OnDestroy {

    subscriptions: Subscription[] = [];
    blockContentFilters: ChartContentFilterModel;

    private readonly name = new FilterTypeTextModel(
        FixedChartSourceFiltersEnum.NAME,
        'Dashboard.field.filter.name'
    );

    private readonly distributionStatus = new FilterTypeCheckboxModel<DistributionStatus>(
        FixedChartSourceFiltersEnum.STATUS,
        'Discover.leftSidebar.distributions.status',
        [
            {
                label: this.localizationService.transform('Discover.deliveryStatusFilter.draft'),
                value: DistributionStatus.draft
            },
            {
                label: this.localizationService.transform('Discover.deliveryStatusFilter.pending'),
                value: DistributionStatus.pending
            },
            {
                label: this.localizationService.transform('Discover.deliveryStatusFilter.scheduled'),
                value: DistributionStatus.scheduled
            },
            {
                label: this.localizationService.transform('Discover.deliveryStatusFilter.sent'),
                value: DistributionStatus.sent
            },
            {
                label: this.localizationService.transform('Discover.deliveryStatusFilter.failed'),
                value: DistributionStatus.failed
            }
        ]
    );

    private readonly searchType = new FilterTypeCheckboxModel<SearchModeEnum>(
        FixedChartSourceFiltersEnum.SEARCH_TYPE,
        'Discover.leftSidebar.filter.searchType',
        [
            {
                label: 'Discover.leftSidebar.filter.searchType.option.contact',
                value: SearchModeEnum.contact
            },
            {
                label: 'Discover.leftSidebar.filter.searchType.option.outlet',
                value: SearchModeEnum.outlet
            }
        ]
    );

    private readonly listType = new FilterTypeCheckboxModel<ListTypeEnum>(
        FixedChartSourceFiltersEnum.LIST_TYPE,
        'Discover.leftSidebar.filter.listType',
        [
            {
                label: 'Discover.leftSidebar.filter.listType.option.both',
                value: ListTypeEnum.both
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.contact',
                value: ListTypeEnum.contact
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.outlet',
                value: ListTypeEnum.outlet
            },
            {
                label: 'Discover.leftSidebar.filter.listType.option.empty',
                value: ListTypeEnum.empty
            },
        ]
    );

    private readonly createdBy = new FilterTypeCheckboxModel<number>(
        FixedChartSourceFiltersEnum.CREATED_BY,
        'createdBy',
        [],
        true
    );

    private readonly modifiedBy = new FilterTypeCheckboxModel<number>(
        FixedChartSourceFiltersEnum.MODIFIED_BY,
        'modifiedBy',
        [],
        true
    );

    private readonly createdDateFilter = new FilterTypeDateQueryModel(
        FixedChartSourceFiltersEnum.DATE_CREATED,
        'dateCreated',
        getDefaultDateInputOptionValuesModel()
    );
    private readonly modifiedDateFilter = new FilterTypeDateQueryModel(
        FixedChartSourceFiltersEnum.DATE_MODIFIED,
        'dateModified',
        getDefaultDateInputOptionValuesModel()
    );

    distributionFilters = [
        this.name,
        this.distributionStatus,
        this.createdBy,
        this.createdDateFilter,
        this.modifiedBy,
        this.modifiedDateFilter
    ];

    distributionSentReportFilters = [
        this.name,
        this.createdBy,
        this.createdDateFilter,
        this.modifiedBy,
        this.modifiedDateFilter
    ];

    targetingActivityFilters = [
        this.name,
        this.searchType,
        this.listType,
        this.createdBy,
        this.createdDateFilter,
        this.modifiedBy,
        this.modifiedDateFilter
    ];

    surgeActivityFilters = [
        this.name,
        this.createdBy,
    ];

    dateFormat: string;

    constructor(
        private distributionService: DistributionService,
        private targetingSearchesService: TargetingSearchesService,
        private targetingListService: TargetingListService,
        private userService: UsersService,
        private currentUserStateService: CurrentUserStateService,
        private localizationService: LocalizationService,
        private eventsService: EventsService,
        private briefingApi: BriefingsApi,
    ) {
        super();
        this.setSubscriptions();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => {
                subscription.unsubscribe();
            }
        );
    }

    initFilters(block: BlockModel): void {
        this.clearFilters();
        const blockType = block.type.key;
        this.blockContentFilters = (block.displayElement as ChartDisplayElementModel).contentFilters;

        switch (blockType) {
            case ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY:
                this.setFilters(this.distributionFilters);
                break;
            case ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS:
                this.setFilters(this.distributionSentReportFilters);
                break;
            case ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY:
                this.setFilters(this.targetingActivityFilters);
                break;
            case ReportBlockTypeEnum.RECENT_SURGE_ALERTS:
                this.setFilters(this.surgeActivityFilters);
                break;
        }

        this.prepopulateFilters(this.blockContentFilters);
    }

    private prepopulateFilters(contentFilters: ChartContentFilterModel): void {
        if (contentFilters.names && contentFilters.names.length > 0) {
            this.name.setValue(contentFilters.names);
        }
        if (contentFilters.status && contentFilters.status.length > 0) {
            this.distributionStatus.setValue(contentFilters.status);
        }
        if (contentFilters.searchType && contentFilters.searchType.length > 0) {
            this.searchType.setValue(contentFilters.searchType);
        }
        if (contentFilters.listType && contentFilters.listType.length > 0) {
            this.listType.setValue(contentFilters.listType);
        }
        if (contentFilters.createdBy && contentFilters.createdBy.length > 0) {
            this.createdBy.setValue(contentFilters.createdBy);
        }
        if (contentFilters.modifiedBy && contentFilters.modifiedBy.length > 0) {
            this.modifiedBy.setValue(contentFilters.modifiedBy);
        }
        if (contentFilters.createdDate && contentFilters.createdDate.type !== DateQueryTypeEnum.ALL) {
            this.createdDateFilter.setValue(contentFilters.createdDate);
        }
        if (contentFilters.modifiedDate && contentFilters.modifiedDate.type !== DateQueryTypeEnum.ALL) {
            this.modifiedDateFilter.setValue(contentFilters.modifiedDate);
        }

        this.filters$.next(this.filters);
    }

    applyFilters(): void {
        super.applyFilters();

        if (this.filters.includes(this.name)) {
            this.blockContentFilters.names = this.name.getValue();
        }

        if (this.filters.includes(this.distributionStatus)) {
            this.blockContentFilters.status = this.distributionStatus.getValue();
        }

        if (this.filters.includes(this.searchType)) {
            this.blockContentFilters.searchType = this.searchType.getValue();
        }

        if (this.filters.includes(this.listType)) {
            this.blockContentFilters.listType = this.listType.getValue();
        }

        if (this.filters.includes(this.createdBy)) {
            this.blockContentFilters.createdBy = this.createdBy.getValue();
        }

        if (this.filters.includes(this.modifiedBy)) {
            this.blockContentFilters.modifiedBy = this.modifiedBy.getValue();
        }

        if (this.filters.includes(this.createdDateFilter)) {
            this.blockContentFilters.createdDate = this.createdDateFilter.getValue();
        }

        if (this.filters.includes(this.modifiedDateFilter)) {
            this.blockContentFilters.modifiedDate = this.modifiedDateFilter.getValue();
        }
    }

    private getFilteredResults(items: FixedSourceResult[], contentFilters: ChartContentFilterModel): FixedSourceResult[] {
        if (contentFilters.names && contentFilters.names.length > 0) {
            items =  items.filter(item => contentFilters.names.some(nameFilter => item.name.toLocaleLowerCase().includes(nameFilter.toLocaleLowerCase())));
        }

        if (contentFilters.status && contentFilters.status.length > 0) {
            items =  items.filter(item => contentFilters.status.some(statusFilter => item.status === statusFilter));
        }

        if (contentFilters.searchType && contentFilters.searchType.length > 0) {
            items =  items.filter(item => contentFilters.searchType.some(searchTypeFilter => {
                let keepItem = item.type === searchTypeFilter;

                // recent targeting activity contains both saved searches results and list results
                // if there're list type filters, we should keep all list results to be filtered later
                if (contentFilters.listType && contentFilters.listType.length > 0 && item.type === undefined) {
                    keepItem = true;
                }

                return keepItem;
            }));
        }

        if (contentFilters.listType && contentFilters.listType.length > 0) {
            items =  items.filter(item => {
                let keepItem = false;
                if (contentFilters.listType.includes(ListTypeEnum.both) && (item.hasContacts && item.hasOutlets)) {
                    keepItem = true;
                }
                if (contentFilters.listType.includes(ListTypeEnum.contact) && (item.hasContacts && !item.hasOutlets)) {
                    keepItem = true;
                }
                if (contentFilters.listType.includes(ListTypeEnum.outlet) && (!item.hasContacts && item.hasOutlets)) {
                    keepItem = true;
                }
                if (contentFilters.listType.includes(ListTypeEnum.empty) && (!item.hasContacts && !item.hasOutlets)) {
                    keepItem = true;
                }

                // recent targeting activity contains both saved searches results and list results
                // if there're saved search type filters, we should keep all saved search results
                if (contentFilters.searchType && contentFilters.searchType.length > 0 && item.type) {
                    keepItem = true;
                }

                return keepItem;
            });
        }

        if (contentFilters.createdBy && contentFilters.createdBy.length > 0) {
            items =  items.filter(item => contentFilters.createdBy.some(createdBy => item.createdByUserId === createdBy));
        }

        if (contentFilters.modifiedBy && contentFilters.modifiedBy.length > 0) {
            items =  items.filter(item => contentFilters.modifiedBy.some(modifiedBy => item.modifiedByUserId === modifiedBy));
        }

        if (contentFilters.createdDate && contentFilters.createdDate.type !== DateQueryTypeEnum.ALL) {
            items =  items.filter(item => DateQueryModel.isInDateQuery(item.createdDate, contentFilters.createdDate));
        }

        if (contentFilters.modifiedDate && contentFilters.modifiedDate.type !== DateQueryTypeEnum.ALL) {
            items =  items.filter(item => DateQueryModel.isInDateQuery(item.modifiedDate, contentFilters.modifiedDate));
        }

        return items;
    }

    setSubscriptions(): void {
        this.subscriptions.push(
            this.currentUserStateService.getAccountUsers().subscribe(users => {
                this.createdBy.options = [];
                this.modifiedBy.options = [];
                users.sort((a, b) => a.getDisplayName().localeCompare(b.getDisplayName()));
                users.forEach(user => {
                    this.createdBy.options.push({
                        label: user.getDisplayName(),
                        value: user.id
                    });

                    this.modifiedBy.options.push({
                        label: user.getDisplayName(),
                        value: user.id
                    });
                });
            }),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => this.dateFormat = prefs.getDateFormatString(true))
        );
    }

    /**
     * Sorts an list by modified date (or created date, if modified date is not available)
     */
    sortByDate(a, b): number {
        return (+b.modifiedDate || +b.createdDate || +b.submittedDate) - (+a.modifiedDate || +a.createdDate || +a.submittedDate);
    }


    /**
     * Some charts like recent targeting activity chart have fixed data source and do not need to set up series
     * Here we uses this method to return data directly
     * If this type of charts continue to grow, ideally we should create its own fixed chart source service
     * @param block the chart block
     * @returns data array
     */
    getListChartData(block: BlockModel): Observable<DataPointModel[]> {
        let res: Observable<DataPointModel[]>;
        const blockType = block.type.key;
        const numOfItems = (block.displayElement as ChartDisplayElementModel).numOfItems;
        const contentFilters = (block.displayElement as ChartDisplayElementModel).contentFilters;

        switch (blockType) {
            case ReportBlockTypeEnum.RECENT_DISTRIBUTION_ACTIVITY:
                res = this.prepareRecentDistributionActivityData(numOfItems, contentFilters);
                break;
            case ReportBlockTypeEnum.RECENT_TARGETING_ACTIVITY:
                res = this.prepareRecentTargetingActivityData(numOfItems, contentFilters);
                break;
            case ReportBlockTypeEnum.RECENT_REPORTS_DISTRIBUTIONS:
                res = this.prepareRecentReportsDistributionsData(numOfItems, contentFilters);
                break;
            case ReportBlockTypeEnum.RECENT_SURGE_ALERTS:
                res = this.prepareRecentSurgeActivityData((block.displayElement as ChartDisplayElementModel));
                break;
        }

        return res;
    }

    private prepareRecentDistributionActivityData(numOfItems: number, contentFilters: ChartContentFilterModel): Observable<DataPointModel[]>{
        let data: DataPointModel[] = [];
        return this.distributionService.getDistributionSummaries().pipe(
            filter(x => !!x),
            map(distributions => {
                data = [];
                const results = this.getFilteredResults(distributions as FixedSourceResult[], contentFilters);
                results.sort(this.sortByDate);
                numOfItems = Math.min(results.length, numOfItems);
                for (let i = 0; i < numOfItems; i++) {
                    const status =  results[i].status;
                    data.push({
                        id: results[i].id.toString(),
                        smallIcon: userEventToIconMap.get(status),
                        date: results[i].modifiedDate ?
                            results[i].modifiedDate.getTime() : results[i].createdDate?.getTime(),
                        title: results[i].name,
                        route: 'Discover/distributions/distribution/' + results[i].id.toString() + (status === DistributionStatus.sent ? '/report' : ''),
                        createdByUserId: results[i].createdByUserId,
                        createdDate: +results[i].createdDate,
                        modifiedByUserId: results[i].modifiedByUserId,
                        modifiedDate: +results[i].modifiedDate,
                        status: status
                    });
                }

                return data.filter(dataItem => dataItem.status === DistributionStatus.scheduled).map(dataItem => +dataItem.id);
            }),
            mergeMap(scheduledIds => {
                // if there's no scheduled distributions, return data directly
                if (scheduledIds.length === 0) {
                    return of (data);
                }

                // fetch scheduled distributions to set scheduled date
                return this.distributionService.getDistributionByIds(scheduledIds).pipe(
                    tap(distributions => {
                        distributions.forEach(distribution => {
                            data.forEach(item => {
                                if (+item.id === distribution.id) {
                                    item.date = +distribution.scheduledDate;
                                }
                            });
                        });
                    })
                );
            }),
            mergeMap(() => {
                // convert user ids to user names, and prepare tooltip
                return this.currentUserStateService.getAccountUsers().pipe(
                    tap(users => {
                        this.convertIdsToUsernames(data, users);
                    })
                );
            }),
            mergeMap(() => {
                return this.currentUserStateService.getPreferences().pipe(
                    filter(x => !!x),
                    map(preferences => {
                        this.prepareTooltipInfo(data, preferences.timeZone);

                        return data;
                    })
                );
            })
        );
    }

    private prepareRecentTargetingActivityData(numOfItems: number, contentFilters: ChartContentFilterModel): Observable<DataPointModel[]>{
        let data: DataPointModel[] = [];
        return combineLatest([
            this.targetingSearchesService.all$.pipe(filter(x => !!x)),
            this.targetingListService.all$.pipe(filter(x => !!x)),
            this.eventsService.searchMine({ resourceType: EventResourceTypeEnum.TARGETING_SEARCH}),
            this.eventsService.searchMine({ resourceType: EventResourceTypeEnum.TARGETING_LIST}),
        ]).pipe(
            map(([searches, lists, eventSearches, eventLists]) => {
                data = [];
                let combinedResults = [];

                searches.forEach(item => {
                    combinedResults.push({
                        id: item.id,
                        name: item.name,
                        type: item.type,
                        createdByUserId: item.createdByUserId,
                        createdDate: item.createdDate,
                        modifiedByUserId: item.modifiedByUserId,
                        modifiedDate: item.modifiedDate,
                        sourceType: UserEventTypesEnum.SAVED_SEARCH
                    });
                });

                lists.forEach(item => {
                    combinedResults.push({
                        id: item.id,
                        name: item.name,
                        hasContacts: item.hasContacts,
                        hasOutlets: item.hasOutlets,
                        createdByUserId: item.createdByUserId,
                        createdDate: item.createdDate,
                        modifiedByUserId: item.modifiedByUserId,
                        modifiedDate: item.modifiedDate,
                        sourceType: UserEventTypesEnum.LIST
                    });
                });

                eventSearches.slice(-numOfItems).forEach(event => {
                    if (event.action === UserEventActionTypeEnum.DELETE) {
                        combinedResults.push({
                            name: event.resourceName,
                            modifiedByUserId: event.userId,
                            modifiedDate: event.date,
                            targetingEventType: TargetingEventsEnum.SAVED_SEARCH_DELETED,
                            sourceType: UserEventTypesEnum.SAVED_SEARCH
                        });
                    }
                });

                eventLists.slice(-numOfItems).forEach(event => {
                    if (event.action === UserEventActionTypeEnum.DELETE) {
                        combinedResults.push({
                            name: event.resourceName,
                            modifiedByUserId: event.userId,
                            modifiedDate: event.date,
                            targetingEventType: TargetingEventsEnum.LIST_DELETED,
                            sourceType: UserEventTypesEnum.LIST
                        });
                    } else {
                        const list = lists.find(x => x.id === event.resourceId );

                        if (list) {
                            let targetingEventType = TargetingEventsEnum.CONTACTS_ADDED_TO_LIST;
                            if (event.action === UserEventActionTypeEnum.REMOVE) {
                                targetingEventType = TargetingEventsEnum.CONTACTS_REMOVED_FROM_LIST;
                            }
                            combinedResults.push({
                                id: list.id,
                                name: list.name,
                                hasContacts: list.hasContacts,
                                hasOutlets: list.hasOutlets,
                                createdByUserId: list.createdByUserId,
                                createdDate: list.createdDate,
                                modifiedByUserId: list.modifiedByUserId,
                                modifiedDate: list.modifiedDate,
                                targetingEventType,
                                sourceType: UserEventTypesEnum.LIST
                            });
                        }
                    }
                });

                combinedResults = this.getFilteredResults(combinedResults, contentFilters);
                combinedResults.sort(this.sortByDate);
                numOfItems = Math.min(combinedResults.length, numOfItems);
                for (let i = 0; i < combinedResults.length; i++) {
                    let eventType = combinedResults[i].targetingEventType;
                    let route;

                    if (combinedResults[i].sourceType === UserEventTypesEnum.SAVED_SEARCH && !eventType) {
                        if (!eventType) {
                            eventType = combinedResults[i].modifiedDate ?
                                TargetingEventsEnum.SAVED_SEARCH_MODIFIED : TargetingEventsEnum.SAVED_SEARCH_CREATED;
                        }
                        if (combinedResults[i].id) {
                            route = `Discover/targetingsearches/search/${combinedResults[i].id}/results`;
                        }
                    } else {
                        if (!eventType) {
                            eventType = combinedResults[i].modifiedDate ?
                                TargetingEventsEnum.LIST_MODIFIED : TargetingEventsEnum.LIST_CREATED;
                        }
                        if (combinedResults[i].id) {
                            route = `Discover/targetinglists/list/${combinedResults[i].id}/results`;
                        }
                    }

                    const dataPoint = {
                        id: combinedResults[i].id,
                        icon: userEventToIconMap.get(eventType),
                        date: combinedResults[i].modifiedDate ?
                            combinedResults[i].modifiedDate.getTime() : combinedResults[i].createdDate?.getTime(),
                        event: `Dashboard.chart.targetingEvent.${eventType}`,
                        title: combinedResults[i].name,
                        userId: combinedResults[i].modifiedDate ? combinedResults[i].modifiedByUserId : combinedResults[i].createdByUserId,
                        route
                    };

                    // Only show the most recent activity of the same entity and type that happens within 8 hour period
                    const matchedData = data.find(x => x.id === dataPoint.id && x.event === dataPoint.event);
                    if (matchedData) {
                        const timeDifference  = Math.abs(Number(matchedData.date) - dataPoint.date);
                        // because combinedResults array is sorted in descending order, no need to compare dates here
                        if (timeDifference >= 28800) {
                            data.push(dataPoint);
                        }
                    } else {
                        data.push(dataPoint);
                    }

                    if (data.length === numOfItems) {
                        break;
                    }
                }

                return data.map(x => +x.userId);
            }),
            mergeMap(() => {
                // convert user id to user name to display
                return this.currentUserStateService.getAccountUsers().pipe(map(users => {
                    this.convertIdsToUsernames(data, users);
                    return data;
                }));
            }),
        );
    }

    private prepareRecentSurgeActivityData(displayElement: ChartDisplayElementModel): Observable<DataPointModel[]>{
        const data: DataPointModel[] = [];
        let numOfItems = displayElement.numOfItems;
        const contentFilters = displayElement.contentFilters;

        const totalHours = DateQueryModel.getTotalHoursInQuery(displayElement.dateRange);
        const earliestDate = moment().subtract(totalHours, 'hours');

        return this.briefingApi.getAllAlertSendHistory().pipe(
            filter(x => !!x),
            map((alertSentDates) => {
                let combinedResults = [];

                alertSentDates.forEach(item => {
                    if (moment(item.submittedDate) > earliestDate) {
                        combinedResults.push({
                            briefingSentId: item.briefingSentId,
                            submittedDate: item.submittedDate,
                            metric: item.metric,
                            briefingId: item.briefingId,
                            createdByUserId: item.createdByUserId,
                            guid: item.guid,
                            surgePercentageChangePositive: item.surgePercentageChangePositive,
                            surgePercentageChangeNegative: item.surgePercentageChangeNegative,
                            name: item.alertName,
                            date: item.submittedDate,
                        });
                    }
                });

                combinedResults = this.getFilteredResults(combinedResults, contentFilters);
                combinedResults.sort(this.sortByDate);
                numOfItems = Math.min(combinedResults.length, numOfItems);
                for (let i = 0; i < combinedResults.length; i++) {
                    data.push({
                        smallIcon: 'fa-kit fa-agpr-momentum',
                        route: `/Monitoring/alerts/alert/${combinedResults[i].briefingId}/${combinedResults[i].guid}/report`,
                        label: `Alerts.surge.metrics.${combinedResults[i].metric}`,
                        name: combinedResults[i].name,
                        title: combinedResults[i].name,
                        briefingSentId: combinedResults[i].briefingSentId,
                        guid: combinedResults[i].guid,
                        submittedDate: combinedResults[i].submittedDate,
                        metric: combinedResults[i].metric,
                        briefingId: combinedResults[i].briefingId,
                        createdByUserId: combinedResults[i].createdByUserId,
                        surgePercentageChangePositive: combinedResults[i].surgePercentageChangePositive as number,
                        surgePercentageChangeNegative: combinedResults[i].surgePercentageChangeNegative as number,
                        date: combinedResults[i].submittedDate,
                    });

                    if (data.length === numOfItems) {
                        break;
                    }
                }

                return data.map(x => +x.userId);
            }),
            mergeMap(() => {
                // convert user id to user name to display
                return this.currentUserStateService.getAccountUsers().pipe(map(users => {
                    this.convertIdsToUsernames(data, users);
                    return data;
                }));
            }),
        );
    }

    private prepareRecentReportsDistributionsData(numOfItems: number, contentFilters: ChartContentFilterModel): Observable<DataPointModel[]>{
        let data: DataPointModel[] = [];
        return this.distributionService.getDistributionSummaries().pipe(
            filter(x => !!x),
            map(distributions => {
                data = [];
                const sentDistributionIds = [];
                distributions = distributions.filter(distribution => distribution.status === DistributionStatus.sent);
                distributions = this.getFilteredResults(distributions, contentFilters) as DistributionSummaryModel[];
                distributions.sort(this.sortByDate);
                numOfItems = Math.min(distributions.length, numOfItems);

                for (let i = 0; i < numOfItems; i++) {
                    sentDistributionIds.push(distributions[i].id);

                    data.push({
                        id: distributions[i].id.toString(),
                        title: distributions[i].name,
                        route: 'Discover/distributions/distribution/' + distributions[i].id.toString() + '/report',
                        createdByUserId: distributions[i].createdByUserId,
                        createdDate: +distributions[i].createdDate,
                        modifiedByUserId: distributions[i].modifiedByUserId,
                        modifiedDate: +distributions[i].modifiedDate,
                        label0: 'Dashboard.chart.recentDistributionReports.delivered',
                        label1: 'Dashboard.chart.recentDistributionReports.opens',
                        label2: 'Dashboard.chart.recentDistributionReports.clicks',
                        label3: 'Dashboard.chart.recentDistributionReports.undelivered',
                        status: DistributionStatus.sent
                    });
                }

                return sentDistributionIds;
            }),
            mergeMap((sentDistributionIds) => {
                // if there's no sent distributions, return empty array
                if (sentDistributionIds.length === 0) {
                    return of ([]);
                }

                return combineLatest([
                    // fetch sent distributions to set sent date
                    this.distributionService.getDistributionByIds(sentDistributionIds).pipe(
                        tap(distributions => {
                            distributions.forEach(distribution => {
                                data.forEach(dataItem => {
                                    if (distribution.id === +dataItem.id) {
                                        dataItem.sentDate = +distribution.sentDate;
                                    }
                                });
                            });
                        })
                    ),

                    // fetch sent distribution reports to set sent, delivers, opens, clicks and fails data
                    this.distributionService.getResourceReports(sentDistributionIds).pipe(
                        tap(reports => {
                            reports.forEach((reportApi, index) => {
                                const report = new RecipientReportDataSeries(reportApi);
                                data[index].total = report.sentCount;
                                data[index].value0 = report.deliveredCount;
                                data[index].value1 = report.uniqueOpenCount;
                                data[index].value2 = report.uniqueClickCount;
                                data[index].value3 = report.blocked;
                            });
                        })
                    ),

                    // convert user id to user names and prepare tooltip
                    this.currentUserStateService.getAccountUsers().pipe(tap(users => {
                        this.convertIdsToUsernames(data, users);
                    }))
                ]);
            }),
            mergeMap(() => {
                return this.currentUserStateService.getPreferences().pipe(
                    filter(x => !!x),
                    tap((preferences) => this.prepareTooltipInfo(data, preferences.timeZone))
                ).pipe(map(() => data));
            })
        );
    }

    private convertIdsToUsernames(data: DataPointModel[], users: UserModel[]): void{
        users.forEach(user => {
            data.forEach(dataItem => {
                if (user.id === dataItem.createdByUserId) {
                    dataItem.createdBy = user.getDisplayName();
                }

                if (user.id === dataItem.modifiedByUserId) {
                    dataItem.modifiedBy = user.getDisplayName();
                }

                if (user.id === dataItem.userId) {
                    dataItem.user = user.getDisplayName();
                }
            });
        });

        // for some system errors or bad data, we set the user name as unknown user
        const unknownUser = this.localizationService.transform('Common.unknownUser');
        data.forEach(dataItem => {
            if (dataItem.userId && !dataItem.user) {
                dataItem.user = unknownUser;
            }

            if (dataItem.createdByUserId && !dataItem.createdBy) {
                dataItem.createdBy = unknownUser;
            }

            if (dataItem.modifiedByUserId && !dataItem.modifiedBy) {
                dataItem.modifiedBy = unknownUser;
            }
        });
    }

    private prepareTooltipInfo(data: DataPointModel[], timeZone: string): void {
        data.forEach(dataItem => {
            let tooltipInfo = this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.created', {
                createdBy: dataItem.createdBy as string,
                createdDate: moment(dataItem.createdDate).tz(timeZone).format(this.dateFormat)
            });

            let tooltipHtml = this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.createdBold', {
                createdBy: dataItem.createdBy as string,
                createdDate: moment(dataItem.createdDate).tz(timeZone).format(this.dateFormat)
            });

            if (dataItem.modifiedDate) {
                tooltipInfo = tooltipInfo + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.modified', {
                    modifiedBy: dataItem.modifiedBy as string,
                    modifiedDate: moment(dataItem.modifiedDate).tz(timeZone).format(this.dateFormat)
                });

                tooltipHtml += this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.modifiedBold', {
                    modifiedBy: dataItem.modifiedBy as string,
                    modifiedDate: moment(dataItem.modifiedDate).tz(timeZone).format(this.dateFormat)
                });
            }

            if (dataItem.status === DistributionStatus.sent || dataItem.status === DistributionStatus.scheduled) {
                tooltipInfo = tooltipInfo + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentBy', {
                    sentBy: (dataItem.modifiedDate ? dataItem.modifiedBy : dataItem.createdBy) as string,
                });

                tooltipHtml += this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentByBold', {
                    sentBy:(dataItem.modifiedDate ? dataItem.modifiedBy : dataItem.createdBy) as string,
                });
            }

            if (dataItem.sentDate) {
                tooltipInfo = tooltipInfo + this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentDate', {
                    sentDate: moment(dataItem.sentDate).tz(timeZone).format(this.dateFormat)
                });

                tooltipHtml += this.localizationService.transform('Dashboard.chart.distributionEvent.tooltipInfo.sentDateBold', {
                    sentDate: moment(dataItem.sentDate).tz(timeZone).format(this.dateFormat)
                });
            }

            delete dataItem.createdByUserId;
            delete dataItem.createdBy;
            delete dataItem.createdDate;
            delete dataItem.modifiedByUserId;
            delete dataItem.modifiedBy;
            delete dataItem.modifiedDate;

            dataItem.tooltipInfo = tooltipInfo;
            dataItem.tooltipHtml = tooltipHtml;
        });
    }
}


